<template>
    <b-modal
        id="modal-legal"
        ref="legalModal"
        size="lg"
        centered
        scrollable
        title="Legal, Terms & Compliance"
        header-close-label="Close"
    >
        <template #modal-title>
            <div class="d-flex align-items-center">
                <i class="bi bi-shield-check text-primary mr-2" style="font-size: 1.25rem;"></i>
                <span class="font-weight-bold">VelocyTrack &mdash; Legal, Terms &amp; Compliance</span>
            </div>
        </template>

        <b-tabs v-model="activeTabIndex" pills justified class="mb-3 legal-tabs">
            <!-- TAB 1: Terms of Service -->
            <b-tab title="Terms of Service">
                <div class="legal-content py-2">
                    <div class="d-flex justify-content-between align-items-start flex-wrap mb-3">
                        <div>
                            <h5 class="font-weight-bold mb-1">Beta Terms of Service, Security Architecture &amp; Limitation of Liability</h5>
                            <div class="text-muted small">
                                <strong>Applicability:</strong> Public Beta / Early Access Release &bull; <strong>Version:</strong> 1.0.0-beta
                            </div>
                        </div>
                        <span class="text-muted small mt-1 mt-sm-0"><strong>Effective Date:</strong> October 20, 2026</span>
                    </div>

                    <div class="legal-alert-box p-3 rounded mb-3">
                        <i class="bi bi-info-circle-fill text-info mr-2"></i>
                        <strong>Important Summary:</strong> VelocyTrack operates strictly as an on-the-fly execution and query proxy. We never ingest, clone, cache, or store your repository source code, branch contents, or pull request payloads. Our order process is conducted by Creem as Merchant of Record. By using the platform, you acknowledge our Beta &ldquo;AS-IS&rdquo; status, limitations of liability (&euro;50.00 EUR / 1-month cap), and governing law (Republic of Bulgaria).
                    </div>

                    <h6>1. Acceptance of Terms, Professional Use &amp; Consumer Exemption Representation</h6>
                    <p>
                        The Service is intended and provided strictly for <strong>professional, commercial, software engineering, and developer workflow purposes (B2Pro)</strong>. By creating an account, authenticating third-party developer platforms, or entering credentials, the User explicitly represents and warrants that: (a) they are accessing and using the Service in their professional capacity as a software developer, engineer, contractor, or freelancer, and not as a consumer for personal, domestic, or household leisure purposes; and (b) to the maximum extent permitted by applicable law, consumer protection regulations providing non-waivable statutory terms or cooling-off rights shall not apply to this agreement.
                    </p>
                    <p>
                        By accessing, browsing, registering for, or using VelocyTrack ("the Service", "the Platform", "the Service Operator", "we", "our", or "us"), 
                        you ("Customer", "User", "you") agree to be bound by these Terms of Service. If you access or use the Service on behalf 
                        of a company, organization, or other legal entity, you represent and warrant that you have the full legal authority 
                        to bind that entity to these Terms. If you do not agree to these Terms, you must not access or use the Service.
                    </p>

                    <h6>2. Nature of the Service &amp; Beta Status</h6>
                    <p>
                        The software and associated platform (the "Service") are currently provided in a <strong>Beta / Early Access</strong> state for testing, validation, and evaluation purposes.
                    </p>
                    <ul>
                        <li>
                            <strong>&ldquo;AS-IS&rdquo; and &ldquo;AS-AVAILABLE&rdquo; Provision:</strong> The Service is provided on a strictly <strong>&ldquo;AS-IS&rdquo;, &ldquo;AS-AVAILABLE&rdquo;, and &ldquo;WITH ALL FAULTS&rdquo;</strong> basis. To the maximum extent permitted by applicable law, the Service Operator expressly disclaims all warranties, express, statutory, or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, title, quiet enjoyment, non-infringement, uptime, continuous availability, or error-free operation.
                        </li>
                        <li>
                            <strong>Feature Modification &amp; Deprecation:</strong> The Service Operator reserves the right to modify, restrict, pause, or permanently terminate features, API endpoints, tiers, or database structures at any time without prior notice or compensation.
                        </li>
                    </ul>

                    <h6>3. Data Processing &amp; Architectural Boundary (&ldquo;On-The-Fly&rdquo; Proxy Model)</h6>
                    <ul>
                        <li>
                            <strong>Zero Source Code Persistence:</strong> The Service acts strictly as an on-the-fly execution and query proxy between the User's authenticated client and third-party version control / project tracking platforms (e.g., GitHub, GitLab, Azure DevOps, Jira). You and your organization retain full, unencumbered ownership, copyright, and intellectual property rights in and to all repository source code, commits, pull requests, work items, and business data.
                        </li>
                        <li>
                            <strong>No Repository Ingestion:</strong> The Service does <strong>not</strong> ingest, mirror, clone, cache, train machine learning (ML) or artificial intelligence (AI) models on, or permanently store customer source code, branch contents, pull request payloads, or corporate proprietary documentation on Service infrastructure.
                        </li>
                        <li>
                            <strong>Transient Processing:</strong> Any data retrieved from third-party APIs is processed in volatile memory (RAM) solely for the immediate lifecycle of the active API request and is discarded immediately after transmitting the response to the user interface.
                        </li>
                    </ul>

                    <h6>4. Authentication, Token Handling &amp; Corporate Repository Warranty of Authority</h6>
                    <ul>
                        <li>
                            <strong>Warranty of Authority (Employer &amp; Client Repositories):</strong> If the User integrates third-party credentials or Personal Access Tokens (PATs) granting access to repositories owned by an employer, client, or external organization, the User warrants that: (a) they possess explicit authorization and rights to access and query those repositories via this Service; and (b) using the Service does not violate any employment contract, contractor agreement, NDA, or workplace IT security policy.
                        </li>
                        <li>
                            <strong>OAuth &amp; Personal Access Tokens (PAT):</strong> OAuth access tokens and API secrets granted by the User are stored in an encrypted state at rest using industry-standard cryptography (AES-256). The Service Operator applies tenant-isolation logic to ensure tokens are securely bounded to the authenticated tenant context.
                        </li>
                        <li>
                            <strong>User Credential Safeguards:</strong> The User retains sole responsibility for maintaining the confidentiality of their account credentials, session cookies, and workspace keys. The User is solely responsible for configuring the <strong>Principle of Least Privilege</strong> on third-party provider accounts (e.g., granting read-only permissions wherever write permissions are not strictly necessary).
                        </li>
                        <li>
                            <strong>Immediate Revocation Right:</strong> The User may revoke OAuth grants, personal access tokens, or workspace credentials at any time directly through their respective third-party provider settings or the VelocyTrack UI. <strong>The Service Operator is not responsible for token usage prior to effective revocation.</strong>
                        </li>
                    </ul>

                    <h6>5. Third-Party Platform Dependencies &amp; Developer-Cockpit Operations</h6>
                    <p>
                        VelocyTrack is an independent software tool and is not affiliated with, endorsed by, or sponsored by Microsoft, GitHub, GitLab, or Atlassian. Our ability to provide backlog synchronization, branch tracking, and pull request intelligence depends entirely upon the availability, rate limits, API schemas, and uptime of these third-party platforms. <strong>The Service Operator is not responsible or liable for any degradation, downtime, API schema deprecation, rate-limiting, or service interruptions caused by third-party providers.</strong>
                    </p>
                    <p>
                        <strong>YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT YOU ARE SOLELY RESPONSIBLE FOR ALL ACTIONS, MODIFICATIONS, SPRINT STATUS OVERRIDES, TIME LOGS, AND REMOTE BRANCH DELETIONS EXECUTED THROUGH YOUR CONNECTED CREDENTIALS.</strong> Under no circumstances shall the Service Operator or its contributors be held liable for lost source code commits, accidentally deleted remote branches, corrupted work item hierarchies, or inaccurate sprint accounting resulting from user error, automated sync, or misconfigured permission scopes.
                    </p>

                    <h6>6. Merchant of Record (MoR) &amp; Billing Terms</h6>
                    <p>
                        Our order and payment process is conducted by <strong>Creem</strong>, who acts as the Merchant of Record for all orders. 
                        Creem handles all billing transactions, payment gateway processing, customer invoicing, global tax compliance (including VAT/GST/sales tax collection), 
                        refunds, and chargebacks. When purchasing a paid workspace or seat license tier, you authorize Creem to charge your designated payment method on a recurring 
                        subscription basis. Workspace owners are financially responsible for all assigned seat capacity and active contributor licenses under their account.
                    </p>

                    <h6>7. Limitation of Liability &amp; Consequential Damages Waiver</h6>
                    <p>
                        <strong>TO THE FULLEST EXTENT PERMITTED BY APPLICABLE JURISDICTION:</strong>
                    </p>
                    <p>
                        <strong>1. Exclusion of Consequential and Indirect Damages:</strong> In no event shall the Service Operator, its founders, directors, officers, employees, contributors, affiliates, or licensors be held liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, including but not limited to:
                    </p>
                    <ul>
                        <li>Loss of profits, revenue, contracts, reputation, goodwill, or commercial opportunities.</li>
                        <li>Business interruption, work stoppage, operational delay, or downtime.</li>
                        <li>Loss, alteration, exposure, or corruption of source code, intellectual property, databases, configuration files, or logs.</li>
                        <li>Unauthorized access, security breaches, credential compromise, or lateral movement arising from external attacks, third-party provider outages, or implementation vulnerabilities.</li>
                        <li>Damages resulting from reliance on data processed through the Service.</li>
                    </ul>
                    <p>
                        <strong>2. Total Aggregate Liability Cap:</strong> The aggregate financial liability of the Service Operator for all claims arising out of or related to the Service&mdash;whether in contract, tort (including negligence), strict liability, indemnity, breach of statutory duty, or otherwise&mdash;shall be strictly limited to the <strong>greater of</strong>:
                    </p>
                    <ul>
                        <li>The total aggregate amount paid by the User to the Service Operator for access to the Service during the <strong>one (1) month</strong> immediately preceding the event giving rise to the claim; or</li>
                        <li><strong>&euro;50.00 (Fifty Euros)</strong>.</li>
                    </ul>

                    <h6>8. Indemnification</h6>
                    <p>
                        The User agrees to defend, indemnify, and hold harmless the Service Operator, its officers, directors, employees, contributors, and agents against any third-party claims, liabilities, damages, losses, costs, or expenses (including reasonable legal and attorney fees) arising out of or related to:
                    </p>
                    <ol>
                        <li>The User's breach of these Terms.</li>
                        <li>The User's violation of applicable data protection laws (including GDPR).</li>
                        <li>The User's misuse of credentials or unauthorized connection of corporate repositories that the User does not possess legal authority to integrate, including any claims, losses, or legal disputes asserted by third-party repository owners, employers, or clients resulting from the User's credentials or integration choices.</li>
                        <li>Any claims asserted by third parties regarding intellectual property exposed, transferred, or processed through the User's credentials.</li>
                    </ol>

                    <h6>9. Account Termination &amp; Suspension</h6>
                    <p>
                        You may terminate your account at any time through your Profile settings. We reserve the right, in our sole discretion, to suspend or terminate 
                        your access to all or part of the Service immediately, without prior notice or liability, in the event of any suspected breach of these Terms, 
                        security vulnerabilities, or abusive high-frequency API usage.
                    </p>

                    <h6>10. Modifications to Terms &amp; Electronic Notice</h6>
                    <p>
                        We may revise and update these Terms of Service, the Privacy Policy, or the Acceptable Use Policy from time to time to reflect architectural improvements, new platform features, or legal and regulatory requirements. When we make changes, we will update the &ldquo;Version&rdquo; and &ldquo;Effective Date&rdquo; at the top of the applicable document and notify registered users via email to the address associated with their account or via an in-app notification prior to the effective date. Your continued access to or use of the Service after the effective date of any updated Terms constitutes your binding acceptance of the revised Terms. If you do not agree to the updated Terms, you must stop using the Service and may terminate your account in Profile settings.
                    </p>

                    <h6>11. Governing Law &amp; Dispute Resolution</h6>
                    <p>
                        These Terms and any non-contractual obligations arising out of or in connection with them shall be governed by and construed in accordance with the <strong>substantive laws of the Republic of Bulgaria</strong>, without giving effect to any principles of conflicts of law. Any dispute, controversy, or claim arising under these Terms shall be subject to the <strong>exclusive jurisdiction of the competent courts of Sofia, Bulgaria</strong>. If any provision of these Terms is found unenforceable, that provision will be severed and the remaining provisions will continue in full force and effect.
                    </p>
                </div>
            </b-tab>

            <!-- TAB 2: Privacy Policy -->
            <b-tab title="Privacy Policy">
                <div class="legal-content py-2">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h5 class="font-weight-bold mb-0">Privacy Policy (GDPR / CCPA)</h5>
                        <span class="text-muted small">Effective Date: October 20, 2026</span>
                    </div>

                    <h6>1. Introduction &amp; Roles (Data Controller vs. Processor)</h6>
                    <p>
                        VelocyTrack is committed to robust data protection and privacy compliance, including the General Data Protection Regulation (GDPR) 
                        and the California Consumer Privacy Act (CCPA).
                    </p>
                    <p>
                        For workspace account management, VelocyTrack acts as the <strong>Data Controller</strong>. When querying external project backlogs, 
                        pull requests, and work items from your organization's connected repositories via our on-the-fly proxy, your company or organization acts as the 
                        <strong>Data Controller</strong>, and VelocyTrack acts as the <strong>Data Processor</strong> operating strictly under your authenticated instructions.
                    </p>

                    <h6>2. Information We Collect &amp; Persist</h6>
                    <ul>
                        <li>
                            <strong>Account Profile Details:</strong> When registering or authenticating via Google OAuth, Microsoft, GitHub, GitLab, or LinkedIn, 
                            we receive your email address, display name, and avatar picture. We never receive or store your third-party account passwords.
                        </li>
                        <li>
                            <strong>Integration Credentials (PATs &amp; OAuth Tokens):</strong> To query sprint tasks and pull requests with platforms such as 
                            Azure DevOps, GitHub, GitLab, and Jira, you may provide Personal Access Tokens or authorize OAuth connections. All credentials are stored in an encrypted state at rest using industry-standard 
                            <strong>AES-256 cryptography</strong> and are bounded to your authenticated tenant workspace context.
                        </li>
                        <li>
                            <strong>User-Created Time Logs &amp; Workspace Settings:</strong> Time entries explicitly logged in VelocyTrack (work item ID reference, date, hours, activity category, and note) along with workspace configuration preferences.
                        </li>
                        <li>
                            <strong>Waitlist &amp; Inquiries:</strong> Contact email address, full name, role, and platform preferences submitted via our waitlist or feedback forms.
                        </li>
                    </ul>

                    <h6>3. What We Do NOT Collect or Store (&ldquo;On-The-Fly&rdquo; Proxy Model)</h6>
                    <ul>
                        <li>
                            <strong>Zero Source Code or Repository Persistence:</strong> VelocyTrack acts strictly as an on-the-fly execution and query proxy. We do <strong>not</strong> ingest, mirror, clone, cache, or permanently store your repository source code, branch contents, pull request payloads, or corporate proprietary documentation on our database or disks.
                        </li>
                        <li>
                            <strong>Transient RAM Processing Only:</strong> Third-party backlog items, pull request diffs, and branch metadata retrieved from external APIs are processed in volatile memory (RAM) solely for the immediate lifecycle of the active API request and are discarded immediately after transmitting the response to your browser.
                        </li>
                        <li>
                            <strong>No AI / ML Model Training:</strong> We never train artificial intelligence or machine learning models on your private code, commit histories, pull requests, or task descriptions.
                        </li>
                        <li>
                            <strong>No Data Monetization:</strong> We never sell, rent, or trade your personal data, developer metrics, or organization telemetry to data brokers or third parties.
                        </li>
                    </ul>

                    <h6>4. Data Storage, Security &amp; Sub-processors</h6>
                    <p>
                        All API and web communications enforce TLS 1.3 encryption in transit. Sensitive credentials are encrypted at rest via AES-256. 
                        Authentication cookies are configured with <code>Secure</code>, <code>HttpOnly</code>, and <code>SameSite</code> flags. 
                        Tenant workspaces are logically segregated using multi-tenant boundaries (<code>WorkspaceId</code>).
                    </p>
                    <p>We utilize trusted infrastructure sub-processors:</p>
                    <ul>
                        <li><strong>Cloudflare:</strong> Edge CDN delivery, DDoS mitigation, and privacy-first web telemetry.</li>
                        <li><strong>Hetzner Online GmbH (Backend &amp; Database Hosting):</strong> The API and encrypted PostgreSQL database are hosted in ISO/IEC 27001-certified data centers in the European Union (Nuremberg, Germany) operated by Hetzner Online GmbH under a GDPR-compliant Data Processing Agreement (DPA).</li>
                        <li><strong>Resend:</strong> Transactional email delivery (beta invitations, account verification, and notification emails).</li>
                        <li><strong>Creem:</strong> Merchant of Record processing subscription payments, billing records, and tax compliance.</li>
                    </ul>

                    <h6>5. Your GDPR &amp; Privacy Rights</h6>
                    <p>
                        Under GDPR and applicable international privacy laws, you possess the right to:
                    </p>
                    <ul>
                        <li>Access, inspect, and export your personal data in a standard machine-readable format.</li>
                        <li>Rectify inaccurate personal profile or workspace settings.</li>
                        <li>Request permanent deletion (&ldquo;Right to be Forgotten&rdquo;) of your account and associated time records.</li>
                        <li>Revoke Personal Access Tokens or OAuth authorizations at any moment, either through the VelocyTrack UI or directly in your Azure DevOps, GitHub, GitLab, or Jira provider settings.</li>
                    </ul>
                    <p>
                        To exercise these rights, navigate to Profile &gt; Account Settings or contact our Data Protection representative at <code>privacy@velocytrack.com</code>.
                    </p>

                    <h6>6. Changes to this Policy</h6>
                    <p>
                        We may update this Privacy Policy periodically to reflect platform enhancements, sub-processor updates, or regulatory changes. When we make updates, we will revise the &ldquo;Effective Date&rdquo; at the top of this policy and notify registered users via email or an in-app notification prior to the effective date. Your continued use of the Service after the effective date constitutes your acknowledgment of the updated Privacy Policy.
                    </p>
                </div>
            </b-tab>

            <!-- TAB 3: Acceptable Use & Security -->
            <b-tab title="Acceptable Use & Security">
                <div class="legal-content py-2">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h5 class="font-weight-bold mb-0">Acceptable Use &amp; Security Policy</h5>
                        <span class="text-muted small">Effective Date: October 20, 2026</span>
                    </div>

                    <h6>1. Purpose &amp; Scope</h6>
                    <p>
                        This Acceptable Use &amp; Security Policy (AUP) establishes operational rules and architectural boundaries to ensure the security, reliability, and integrity 
                        of VelocyTrack and connected developer ecosystems.
                    </p>

                    <h6>2. Prohibited Activities</h6>
                    <p>You agree NOT to use or attempt to use VelocyTrack to:</p>
                    <ul>
                        <li>
                            <strong>Unauthorized Corporate Repository Connection:</strong> Connect corporate or third-party repositories, organizations, or project spaces that you do not possess full legal authority to integrate.
                        </li>
                        <li>
                            <strong>API &amp; Rate Limit Abuse:</strong> Execute automated high-frequency scripts, unauthorized scraping, or denial-of-service 
                            floods against VelocyTrack or external integration endpoints (Azure DevOps, GitHub, GitLab, Jira).
                        </li>
                        <li>
                            <strong>Credential &amp; Tenant Sharing:</strong> Share workspace access credentials, session cookies, or multi-tenant API tokens 
                            across unauthorized third parties or competing entities.
                        </li>
                        <li>
                            <strong>Malicious Code &amp; Repositories:</strong> Connect repositories containing malware, illicit exploitation payloads, 
                            viruses, or material that infringes upon third-party intellectual property rights.
                        </li>
                        <li>
                            <strong>Reverse Engineering:</strong> Decompile, reverse-engineer, disassemble, or extract proprietary algorithms 
                            powering our visualization engines, capacity calculations, or time intelligence services.
                        </li>
                        <li>
                            <strong>Security Probing:</strong> Perform unauthorized penetration testing, vulnerability scanning, or security audits 
                            without prior written authorization from VelocyTrack.
                        </li>
                    </ul>

                    <h6>3. User Credential Safeguards &amp; Principle of Least Privilege</h6>
                    <p>
                        The User retains sole responsibility for maintaining the confidentiality of their account credentials, session cookies, and workspace keys, and for configuring the <strong>Principle of Least Privilege</strong> on third-party provider accounts (granting read-only permissions wherever write permissions are not strictly necessary; typically <em>Work Items Read &amp; Write</em> and <em>Code Read</em>). Do not configure Full Administrator PATs.
                    </p>

                    <h6>4. Immediate Token Revocation Right</h6>
                    <p>
                        You retain full sovereignty over your integrated services. You may delete your stored PAT or disconnect OAuth grants from the VelocyTrack UI at any time, 
                        or revoke permissions directly within your Azure DevOps, GitHub, GitLab, or Jira account settings, which immediately and irrevocably 
                        terminates VelocyTrack's access to that service. The Service Operator is not responsible for token usage prior to effective revocation.
                    </p>

                    <h6>5. Responsible Vulnerability Disclosure</h6>
                    <p>
                        If you believe you have discovered a security vulnerability in VelocyTrack, please report it immediately to 
                        <code>security@velocytrack.com</code>. We ask that you provide a reasonable opportunity to remediate the issue prior to public disclosure.
                    </p>
                </div>
            </b-tab>
        </b-tabs>

        <template #modal-footer="{ ok }">
            <div class="d-flex justify-content-between align-items-center w-100 flex-wrap">
                <span class="text-muted small mb-2 mb-sm-0">
                    <i class="bi bi-shield-check mr-1 text-success"></i>Enterprise Compliance &bull; Inquiries: <a href="mailto:support@velocytrack.com" class="text-info text-decoration-none">support@velocytrack.com</a>
                </span>
                <b-button variant="primary" size="sm" class="px-3 font-weight-bold" @click="ok">
                    Understood
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script>
    import { BModal, BTabs, BTab, BButton } from 'bootstrap-vue';
    import eventBus from '../event-bus';

    export default {
        name: 'ModalLegal',
        components: {
            BModal,
            BTabs,
            BTab,
            BButton
        },
        data() {
            return {
                activeTabIndex: 0
            };
        },
        created() {
            eventBus.$on('open-legal', this.showTab);
            eventBus.$on('open-legal-modal', this.showTab);
        },
        beforeUnmount() {
            eventBus.$off('open-legal', this.showTab);
            eventBus.$off('open-legal-modal', this.showTab);
        },
        methods: {
            show(tab = 'terms') {
                const setIndex = () => {
                    if (tab === 'privacy') {
                        this.activeTabIndex = 1;
                    } else if (tab === 'security' || tab === 'aup' || tab === 'acceptable-use') {
                        this.activeTabIndex = 2;
                    } else {
                        this.activeTabIndex = 0; // terms
                    }
                };
                setIndex();
                this.$bvModal.show('modal-legal');
                this.$nextTick(setIndex);
            },
            showTab(tab) {
                this.show(tab);
            }
        }
    };
</script>

<style scoped>
    .legal-tabs ::v-deep .nav-pills .nav-link {
        font-weight: 600;
        font-size: 0.9rem;
        padding: 0.5rem 0.8rem;
    }

    .legal-content {
        max-height: 55vh;
        overflow-y: auto;
        font-size: 0.88rem;
        line-height: 1.6;
        color: #334155;
    }

    [data-theme="dark"] .legal-content {
        color: #cbd5e1;
    }

    .legal-content h5 {
        color: #0f172a;
    }

    [data-theme="dark"] .legal-content h5 {
        color: #f8fafc;
    }

    .legal-content h6 {
        font-weight: 700;
        margin-top: 1.25rem;
        margin-bottom: 0.35rem;
        color: #1e293b;
        font-size: 0.92rem;
    }

    [data-theme="dark"] .legal-content h6 {
        color: #f1f5f9;
    }

    .legal-content p, .legal-content ul, .legal-content ol {
        margin-bottom: 0.85rem;
    }

    .legal-content ul, .legal-content ol {
        padding-left: 1.25rem;
    }

    .legal-content li {
        margin-bottom: 0.4rem;
    }

    .legal-alert-box {
        background-color: rgba(14, 165, 233, 0.08);
        border: 1px solid rgba(14, 165, 233, 0.25);
        font-size: 0.85rem;
        line-height: 1.5;
        color: #0369a1;
    }

    [data-theme="dark"] .legal-alert-box {
        background-color: rgba(56, 189, 248, 0.1);
        border-color: rgba(56, 189, 248, 0.25);
        color: #7dd3fc;
    }

    .legal-content code {
        font-size: 0.85em;
        background-color: rgba(0, 0, 0, 0.05);
        padding: 0.15rem 0.35rem;
        border-radius: 4px;
        color: #0f172a;
    }

    [data-theme="dark"] .legal-content code {
        background-color: rgba(255, 255, 255, 0.1);
        color: #f1f5f9;
    }
</style>

<style>
    /* Ensure Legal Modal layers above other modals when opened from welcome / onboarding */
    .modal#modal-legal {
        z-index: 1065 !important;
    }
</style>
